import React, { useState } from 'react';

interface ErrorDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  details?: string;
  errorCode?: string;
  timestamp?: string;
  onClose: () => void;
  onRetry?: () => void;
}

const ErrorDialog: React.FC<ErrorDialogProps> = ({
  isOpen,
  title,
  message,
  details,
  errorCode,
  timestamp,
  onClose,
  onRetry
}) => {
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copying' | 'success' | 'error'>('idle');

  if (!isOpen) return null;

  const handleCopyToClipboard = async () => {
    setCopyStatus('copying');
    
    const errorInfo = {
      title,
      message,
      details,
      errorCode,
      timestamp: timestamp || new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    };
    
    const errorText = `
PDF Form Builder Error Report
============================
Title: ${errorInfo.title}
Message: ${errorInfo.message}
${errorInfo.details ? `Details: ${errorInfo.details}` : ''}
${errorInfo.errorCode ? `Error Code: ${errorInfo.errorCode}` : ''}
Timestamp: ${errorInfo.timestamp}
URL: ${errorInfo.url}
User Agent: ${errorInfo.userAgent}
============================
    `.trim();

    try {
      await navigator.clipboard.writeText(errorText);
      setCopyStatus('success');
      setTimeout(() => setCopyStatus('idle'), 3000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      setCopyStatus('error');
      setTimeout(() => setCopyStatus('idle'), 3000);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>
      
      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="bg-red-50 px-6 py-4 border-b border-red-200 rounded-t-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.232 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-red-900">{title}</h3>
              </div>
              <div className="ml-auto">
                <button
                  onClick={onClose}
                  className="text-red-400 hover:text-red-600 transition-colors"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            <div className="space-y-4">
              {/* Main Message */}
              <div>
                <p className="text-gray-900 text-sm font-medium mb-2">What happened:</p>
                <p className="text-gray-700 text-sm">{message}</p>
              </div>

              {/* Error Code */}
              {errorCode && (
                <div>
                  <p className="text-gray-900 text-sm font-medium mb-1">Error Code:</p>
                  <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono text-red-600">
                    {errorCode}
                  </code>
                </div>
              )}

              {/* Technical Details */}
              {details && (
                <div>
                  <p className="text-gray-900 text-sm font-medium mb-2">Technical Details:</p>
                  <div className="bg-gray-50 border border-gray-200 rounded p-3 max-h-32 overflow-y-auto">
                    <pre className="text-xs text-gray-600 whitespace-pre-wrap font-mono">
                      {details}
                    </pre>
                  </div>
                </div>
              )}

              {/* Timestamp */}
              {timestamp && (
                <div>
                  <p className="text-gray-900 text-sm font-medium mb-1">When:</p>
                  <p className="text-gray-600 text-sm">{new Date(timestamp).toLocaleString()}</p>
                </div>
              )}

              {/* What to do */}
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-blue-900 text-sm font-medium mb-2">What you can do:</p>
                <ul className="text-blue-800 text-sm space-y-1">
                  <li>• Try clicking "Generate PDF" again</li>
                  <li>• Check that field names don't contain periods (.), spaces, or special characters</li>
                  <li>• Make sure you've selected at least one field from the PDF</li>
                  <li>• Wait for field analysis to complete (field names should not show "loading...")</li>
                  <li>• If the problem persists, copy the error details and contact support</li>
                </ul>
              </div>
              
              {/* Additional Help for Specific Errors */}
              {details && details.includes('period character') && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <p className="text-yellow-900 text-sm font-medium mb-2">🔧 Specific Fix Needed:</p>
                  <p className="text-yellow-800 text-sm">
                    The error indicates field names contain periods (.). This typically happens when:
                  </p>
                  <ul className="text-yellow-800 text-sm mt-1 space-y-1">
                    <li>• Field analysis is still in progress (shows "loading...")</li>
                    <li>• Field names have special characters that need to be removed</li>
                    <li>• The backend field sanitization didn't work properly</li>
                  </ul>
                  <p className="text-yellow-800 text-sm mt-2 font-medium">
                    Solution: Wait for field analysis to complete, then try again.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 rounded-b-lg">
            <div className="flex justify-between items-center">
              <button
                onClick={handleCopyToClipboard}
                disabled={copyStatus === 'copying'}
                className={`text-sm transition-colors flex items-center space-x-2 ${
                  copyStatus === 'success' 
                    ? 'text-green-600 hover:text-green-700' 
                    : copyStatus === 'error'
                    ? 'text-red-600 hover:text-red-700'
                    : 'text-gray-600 hover:text-gray-800'
                } ${copyStatus === 'copying' ? 'opacity-50 cursor-not-allowed' : 'underline cursor-pointer'}`}
              >
                {copyStatus === 'copying' && (
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {copyStatus === 'success' && (
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                {copyStatus === 'error' && (
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
                {copyStatus === 'idle' && <span>📋</span>}
                <span>
                  {copyStatus === 'copying' && 'Copying...'}
                  {copyStatus === 'success' && 'Copied to clipboard!'}
                  {copyStatus === 'error' && 'Copy failed - try again'}
                  {copyStatus === 'idle' && 'Copy Error Details'}
                </span>
              </button>
              
              <div className="flex space-x-3">
                {onRetry && (
                  <button
                    onClick={onRetry}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors"
                  >
                    Try Again
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-300 text-gray-700 text-sm font-medium rounded hover:bg-gray-400 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorDialog;